import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MemberDatabaseAddNewProfileComponent } from '../member-database-add-new-profile/member-database-add-new-profile';
import { MemberDatabaseService } from '../shared/services/member-database-public';
import { SharedModule } from '../shared/shared.module';
import { MemberDatabaseApiResponse, MemberDatabasePublic } from '../shared/types/member-database-public.types';

@Component({
    selector: 'fp-member-database-public',
    imports: [SharedModule, MemberDatabaseAddNewProfileComponent],
    templateUrl: './member-database-public.component.html',
    styleUrls: ['./member-database-public.component.scss']
})
export class MemberDatabasePublicComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly memberDatabaseService = inject(MemberDatabaseService);
  private readonly metaService = inject(Meta);
  private readonly linkedInClickedIds = new Set<string>();

  loading = true;
  errorMessage: string | null = null;
  membersData: MemberDatabaseApiResponse | null = null;
  showAddProfileDialog = false;

  readonly pageRows = 12;
  first = 0;

  selectedInterests: number[] = [];
  selectedCountry: number | null = null;
  selectedField: number | null = null;

  interestOptions: { label: string; value: number }[] = [];
  countryOptions: { label: string; value: number }[] = [];
  fieldOptions: { label: string; value: number }[] = [];

  constructor() {
    this.metaService.addTag({ name: 'robots', content: 'noindex,nofollow' });
  }

  ngOnInit(): void {
    this.loadMembers();
  }

  onFilterChange(): void {
    this.first = 0;
    this.selectedInterests = this.selectedInterests ?? [];
    this.loadMembers();
  }

  onPageChange(event: { first?: number }): void {
    const nextFirst = event.first ?? 0;
    if (nextFirst === this.first) {
      return;
    }
    this.first = nextFirst;
    this.loadMembers();
  }

  onProfileSubmitted(): void {
    this.loadMembers();
  }

  get members(): MemberDatabasePublic[] {
    return this.membersData?.results ?? [];
  }

  get totalCount(): number {
    return this.membersData?.count ?? 0;
  }

  get showPaginator(): boolean {
    return this.totalCount > this.pageRows;
  }

  interestLabel(item: { label?: string } | number): string {
    if (typeof item === 'object' && item?.label) {
      return item.label;
    }
    return this.interestOptions.find(option => option.value === item)?.label ?? '';
  }

  getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  linkedinHref(participant: MemberDatabasePublic): string | null {
    const url = participant.member_linkedin?.trim();
    if (!url) {
      return null;
    }
    return url.startsWith('http') ? url : `https://${url}`;
  }

  onLinkedInClick(participant: MemberDatabasePublic): void {
    this.linkedInClickedIds.add(this.participantKey(participant));
  }

  isLinkedInClicked(participant: MemberDatabasePublic): boolean {
    return this.linkedInClickedIds.has(this.participantKey(participant));
  }

  interestsList(participant: MemberDatabasePublic): string[] {
    return (participant.member_interests ?? []).map(interest => interest.trim()).filter(Boolean);
  }

  private loadMembers(): void {
    if (!this.membersData) {
      this.loading = true;
    }

    this.memberDatabaseService
      .getMembers({
        country: this.selectedCountry ?? undefined,
        field: this.selectedField ?? undefined,
        interests: this.selectedInterests?.length ? this.selectedInterests : undefined,
        limit: this.pageRows,
        offset: this.first,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: data => this.setData(data),
        error: () => {
          this.errorMessage = 'Failed to load members';
        },
      });
  }

  private setData(data: MemberDatabaseApiResponse): void {
    this.membersData = data;
    this.interestOptions = data.all_interests ?? [];
    this.countryOptions = data.all_countries ?? [];
    this.fieldOptions = data.all_fields ?? [];
  }

  private participantKey(participant: MemberDatabasePublic): string {
    return String(participant.id);
  }
}
